import Compressor from 'compressorjs';
import UTIF from 'utif';

interface CompressOptions {
  quality?: number;
  convertToJPG?: boolean;
}

async function convertTiffToJpeg(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        if (!e.target?.result) {
          throw new Error('Failed to read file');
        }

        // 解析TIFF文件
        const buffer = e.target.result as ArrayBuffer;
        const ifds = UTIF.decode(buffer);
        if (!ifds || ifds.length === 0) {
          throw new Error('Invalid TIFF file');
        }

        // 将第一帧转换为RGBA数据
        UTIF.decodeImage(buffer, ifds[0]);
        const rgba = UTIF.toRGBA8(ifds[0]);

        // 创建canvas并绘制图像
        const canvas = document.createElement('canvas');
        canvas.width = ifds[0].width;
        canvas.height = ifds[0].height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Failed to get canvas context');
        }

        // 将RGBA数据绘制到canvas
        const imageData = ctx.createImageData(canvas.width, canvas.height);
        imageData.data.set(rgba);
        ctx.putImageData(imageData, 0, 0);

        // 转换为JPEG
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to convert TIFF to JPEG'));
            }
          },
          'image/jpeg',
          0.9,
        );
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

export default async function imageCompressor(file: File, options: CompressOptions = {}) {
  const { quality = 0.6, convertToJPG = true } = options;

  // 检查是否为TIFF文件
  const isTiff =
    file.type === 'image/tiff' ||
    file.name.toLowerCase().endsWith('.tiff') ||
    file.name.toLowerCase().endsWith('.tif');

  // 如果是TIFF文件，先转换为JPEG
  let processedFile = file;
  if (isTiff) {
    try {
      const jpegBlob = await convertTiffToJpeg(file);
      processedFile = new File([jpegBlob], file.name.replace(/\.(tiff|tif)$/i, '.jpg'), {
        type: 'image/jpeg',
      });
    } catch (error) {
      console.error('TIFF conversion failed:', error);
      throw new Error('Failed to convert TIFF file');
    }
  }

  return new Promise((resolve, reject) => {
    new Compressor(processedFile, {
      quality,
      maxWidth: 8192,
      maxHeight: 8192,
      minHeight: 10,
      minWidth: 10,
      mimeType: convertToJPG ? 'image/jpeg' : 'auto',
      convertSize: 0,
      beforeDraw(context) {
        context.fillStyle = '#F1F2F5';
        context.fillRect(0, 0, context.canvas.width, context.canvas.height);
      },
      success(result) {
        resolve(result);
      },
      error(error) {
        reject(error);
      },
    });
  });
}
